import { useState } from "react";
import { useDeleteMappingVersion } from "../../../../api/versioningService";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { useInvalidateVersionCaches } from "../../mapping-version/hooks/useInvalidateVersionCaches";
import { VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { VersionHistoryGroup } from "../types/versionHistory.types";
import { resolveVersionLabel } from "../utils/versionHistory.utils";

/**
 * DELETE /mapping-versions/:versionId. Deleting the active version promotes the next newest one
 * in its place server-side, so every version-scoped cache needs the same refresh as activate --
 * see useInvalidateVersionCaches.
 */
export function useVersionDeletion(params: { onDeleted?: () => void; refetchVersions: () => void }) {
  const [versionToDelete, setVersionToDelete] = useState<VersionHistoryGroup | null>(null);
  const { mutate, isLoading } = useDeleteMappingVersion();
  const invalidateVersionCaches = useInvalidateVersionCaches();

  const requestDelete = (version: VersionHistoryGroup) => {
    // The legacy bucket has no version document to delete.
    if (!version.versionId) return;
    setVersionToDelete(version);
  };

  const cancelDelete = () => setVersionToDelete(null);

  const confirmDelete = () => {
    if (!versionToDelete?.versionId) return;

    const label = resolveVersionLabel(versionToDelete);
    mutate(
      { versionId: versionToDelete.versionId },
      {
        onSuccess: () => {
          successMessage(VERSION_HISTORY_COPY.deleteVersionSuccess(label));
          invalidateVersionCaches(versionToDelete.drawingToolId ?? undefined);
          setVersionToDelete(null);
          params.refetchVersions();
          params.onDeleted?.();
        },
        // No local toast here: the axios interceptor already toasts the server's message (or a
        // fallback) for every failed request, so adding one here would show the error twice.
        onError: () => {
          setVersionToDelete(null);
        }
      }
    );
  };

  return { versionToDelete, isDeleting: isLoading, requestDelete, cancelDelete, confirmDelete };
}
